<script lang="ts">
  /**
   * A proportional bar plus a hover card, in `<RoleBreakdown>`'s visual language (2px
   * track, rounded, dot legend). Generic over its segments so all three revenue tiles
   * draw the same object: the plan mix, the at-risk split, and the churned mix.
   *
   * WHY A BAR RATHER THAN A SENTENCE. This started as a run-on sub-line — "78 on monthly
   * plans · $5,373/yr from 9 annual · $36,042 collected all-time" — three unrelated
   * quantities in one string, which is the stat sentence this admin keeps deleting. Where
   * the numbers are a SPLIT of one population, a bar says it in one glance.
   *
   * THE HOVER CARD CARRIES NUMBERS, NOT PROSE. A tooltip that explains is a tooltip you
   * read once and then resent; this one is a table — swatch, label, count, amount — so it
   * rewards a second look. The explaining stays in the tile's `<InfoTip>`, which is where
   * a reader goes when they want the rule rather than the figures.
   *
   * `cursor-pointer` because the bar reveals something on hover. A bar that hides detail
   * behind a hover and still shows a text cursor is a control pretending to be decoration.
   */
  export interface MixSegment {
    key: string;
    label: string;
    /** The proportion this slice takes, and what the hover card counts. */
    n: number;
    color: string;
    /** Optional money for this slice, pre-formatted by the caller — it owns the currency. */
    amount?: string | null;
    /** Open this slice's members. A slice IS a group of people; making the legend the
     *  only way to reach them would hide the most direct route. A callback rather than an
     *  href because the host page may already BE the board, where the right answer is to
     *  re-filter in place, not to navigate to the page you are on. */
    onSelect?: (() => void) | null;
  }

  let {
    segments,
    label,
    /** Rendered in the hover card under the rows — a total, a denominator, a caveat.
     *  Still numbers, not a sentence. */
    footnote = null,
  }: {
    segments: MixSegment[];
    label: string;
    footnote?: string | null;
  } = $props();

  const shown = $derived(segments.filter((s) => s.n > 0));
  const total = $derived(shown.reduce((acc, s) => acc + s.n, 0));
  const pct = (n: number): string => (total ? `${Math.round((n / total) * 100)}%` : '0%');
</script>

{#if total > 0}
  <div class="group relative">
    <div class="eyebrow mb-1 text-[0.55rem] text-base-content/62">{label}</div>

    <!-- `h-2.5` and full width: the bar was 2px in a third of the tile, which made the
         smallest slice invisible — and the smallest slice is usually the one that
         matters (the annual payers, the members with no published price). -->
    <div class="flex h-2.5 w-full cursor-pointer overflow-hidden rounded-full bg-base-200">
      {#each shown as s (s.key)}
        <svelte:element
          this={s.onSelect ? 'button' : 'div'}
          type={s.onSelect ? 'button' : undefined}
          onclick={s.onSelect ?? undefined}
          aria-label={s.onSelect ? `${s.n} ${s.label}` : undefined}
          class="h-full {s.onSelect ? 'cursor-pointer transition-opacity hover:opacity-75' : ''}"
          style:width="{(s.n / total) * 100}%"
          style:background={s.color}
        ></svelte:element>
      {/each}
    </div>

    <div class="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[0.55rem]">
      {#each shown as s (s.key)}
        <svelte:element
          this={s.onSelect ? 'button' : 'span'}
          type={s.onSelect ? 'button' : undefined}
          onclick={s.onSelect ?? undefined}
          class="inline-flex items-center gap-1 {s.onSelect ? 'cursor-pointer hover:underline' : ''}"
          style:color={s.color}
        >
          <span class="inline-block h-1.5 w-1.5 rounded-full" style:background={s.color}></span>{s.n}
          {s.label}
        </svelte:element>
      {/each}
    </div>

    <!-- Hover card. `pointer-events-none` so it can never sit between the cursor and the
         bar and flicker; it is a readout, nothing in it is clickable. -->
    <div
      class="pointer-events-none absolute left-0 top-full z-20 mt-1.5 hidden min-w-[13rem] rounded-box border hair
             bg-base-100 p-2.5 shadow-lg shadow-black/5 group-hover:block"
      role="tooltip"
    >
      <table class="w-full border-collapse font-mono text-[0.7rem]">
        <tbody>
          {#each shown as s (s.key)}
            <tr>
              <td class="py-0.5 pr-2">
                <span class="inline-flex items-center gap-1.5">
                  <span class="inline-block h-1.5 w-1.5 shrink-0 rounded-full" style:background={s.color}></span>
                  <span class="text-base-content/75">{s.label}</span>
                </span>
              </td>
              <!-- Share and headcount stay READABLE — they are the analytics. Only the
                   amount column is redacted, because only it carries money. -->
              <td class="py-0.5 pr-2 text-right tabular-nums text-base-content/62">{pct(s.n)}</td>
              <td class="py-0.5 pr-2 text-right tabular-nums">{s.n}</td>
              <td class="redact py-0.5 text-right tabular-nums text-base-content/75">{s.amount ?? ''}</td>
            </tr>
          {/each}
        </tbody>
      </table>
      {#if footnote}
        <div class="mt-1.5 border-t hair pt-1.5 font-mono text-[0.65rem] text-base-content/62">{footnote}</div>
      {/if}
    </div>
  </div>
{/if}
